import { Component, lazy, Suspense, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError } from "../../lib/api";
import { signupGtm } from "../../lib/gtm";
import { useSession } from "../../lib/session";
import { Button, ErrorNote, Field, Input, Skeleton } from "../../components/ui";
import { Logo } from "../../components/layout/Logo";
import { cn } from "../../lib/cn";

const PrivySignIn = lazy(() => import("../../components/auth/PrivySignIn"));

type Kind = "provider" | "consumer" | "both";

/** A Privy failure (bad app id, blocked script, outage) must never break the page. */
class SignInBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(e: unknown) { console.warn("[auth] email sign-in unavailable:", e); }
  render() {
    return this.state.failed
      ? <p className="rounded-md border border-border bg-bg-elev p-3 text-sm text-fg-2">Sign-in is temporarily unavailable. Please try again in a few minutes.</p>
      : this.props.children;
  }
}

/** Which sign-in this deployment offers: email code via Privy when configured, else email + password (local dev). */
function usePrivyAppId(): { loading: boolean; appId: string | null } {
  const [state, setState] = useState<{ loading: boolean; appId: string | null }>({ loading: true, appId: null });
  useEffect(() => {
    let live = true;
    api.auth.providers().then((p) => live && setState({ loading: false, appId: p.privy?.appId ?? null })).catch(() => live && setState({ loading: false, appId: null }));
    return () => { live = false; };
  }, []);
  return state;
}

function EmailCode(props: { appId: string; defaultKind: Kind; gtm?: Record<string, unknown>; onSignedIn: (r: { orgId?: string; kind?: Kind }) => void | Promise<void> }) {
  return (
    <SignInBoundary>
      <Suspense fallback={<div className="space-y-4"><Skeleton className="h-14 w-full" /><Skeleton className="h-9 w-full" /></div>}>
        <PrivySignIn {...props} />
      </Suspense>
    </SignInBoundary>
  );
}


const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AuthFrame({ title, subtitle, children, footer }: { title: string; subtitle: string; children: ReactNode; footer: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <Logo />
        <h1 className="mt-8 text-xl font-semibold">{title}</h1>
        <p className="mt-1 text-sm text-fg-2">{subtitle}</p>
        <div className="mt-6 rounded-lg border border-border bg-panel p-5">{children}</div>
        <p className="mt-4 text-center text-sm text-fg-2">{footer}</p>
      </div>
    </div>
  );
}

/** Only allow same-origin relative redirects. */
function safeNext(next: string | null, fallback: string) {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : fallback;
}

export function Login() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refresh, setOrgId } = useSession();
  const { loading, appId } = usePrivyAppId();
  const [usePassword, setUsePassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: typeof errors = {};
    if (!EMAIL_RE.test(email.trim())) errs.email = "Enter a valid email address";
    if (!password) errs.password = "Password is required";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    setError(null);
    try {
      await api.auth.login({ email: email.trim(), password });
      setPassword("");
      await refresh();
      nav(safeNext(params.get("next"), "/ask"), { replace: true });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? { message: "Incorrect email or password." } : err);
    } finally {
      setBusy(false);
    }
  };

  const emailCode = !!appId && !usePassword;
  return (
    <AuthFrame
      title="Sign in"
      subtitle={emailCode ? "Enter your email and we'll send you a code. New here? The same steps create your account." : "Access your DBIndex provider and consumer workspaces."}
      footer={emailCode
        ? <>Have a DBIndex password? <button type="button" className="text-accent hover:underline" onClick={() => setUsePassword(true)}>Sign in with it</button></>
        : appId
          ? <button type="button" className="text-accent hover:underline" onClick={() => setUsePassword(false)}>Sign in with an email code instead</button>
          : <>New to DBIndex? <Link to="/signup" className="text-accent hover:underline">Create an account</Link></>}
    >
      {loading ? <Skeleton className="h-28 w-full" /> : emailCode ? (
        <EmailCode
          appId={appId!}
          defaultKind="provider"
          onSignedIn={async ({ orgId, kind }) => {
            if (orgId) setOrgId(orgId);
            await refresh();
            nav(safeNext(params.get("next"), orgId ? (kind === "consumer" ? "/consumer" : "/sources/new") : "/ask"), { replace: true });
          }}
        />
      ) : (
        <form onSubmit={submit} noValidate className="space-y-4">
          <Field label="Email" error={errors.email}>
            {(p) => <Input {...p} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />}
          </Field>
          <Field label="Password" error={errors.password}>
            {(p) => <Input {...p} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />}
          </Field>
          <ErrorNote error={error} />
          <Button type="submit" variant="primary" className="w-full" loading={busy}>
            Sign in
          </Button>
        </form>
      )}
    </AuthFrame>
  );
}

const KINDS = [
  { v: "provider", t: "Publish data", d: "Data provider" },
  { v: "consumer", t: "Query data", d: "AI / app consumer" },
  { v: "both", t: "Both", d: "Provider and consumer" },
] as const;

export function Signup() {
  const nav = useNavigate();
  const { refresh, setOrgId } = useSession();
  const [params] = useSearchParams();
  // GTM entry points: /signup?kind=consumer (developers), ?kind=provider&program=founding_partner&invite=… (data owners), ?ref=CODE, ?next=/path
  const kindParam = params.get("kind");
  const program = params.get("program") === "founding_partner" || !!params.get("invite") ? ("founding_partner" as const) : undefined;
  const next = params.get("next");
  const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : null;
  const [f, setF] = useState({ name: "", email: "", password: "", orgName: "", orgKind: (kindParam === "consumer" || kindParam === "both" ? kindParam : "provider") as "provider" | "consumer" | "both" });
  const { loading, appId } = usePrivyAppId();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!f.name.trim()) errs.name = "Your name is required";
    if (!EMAIL_RE.test(f.email.trim())) errs.email = "Enter a valid email address";
    if (f.password.length < 12) errs.password = "Use at least 12 characters";
    if (f.orgName.trim().length < 2) errs.orgName = "Organisation name is required";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    setError(null);
    try {
      const gtm = signupGtm({ program: f.orgKind !== "consumer" ? program : undefined, invite: params.get("invite"), ref: params.get("ref") });
      const r = await api.auth.signup({ ...f, email: f.email.trim(), name: f.name.trim(), orgName: f.orgName.trim(), gtm });
      setF((s) => ({ ...s, password: "" }));
      setOrgId(r.org.id);
      await refresh();
      nav(safeNext ?? (f.orgKind === "consumer" ? "/consumer" : "/sources/new"), { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const title = program && f.orgKind !== "consumer" ? "Become a Founding Data Partner" : f.orgKind === "consumer" ? "Get your API key and free credits" : "Create your account";
  if (loading) return <AuthFrame title={title} subtitle="" footer={null}><Skeleton className="h-28 w-full" /></AuthFrame>;
  if (appId) {
    return (
      <AuthFrame title={title} subtitle="Enter your email to get started." footer={<>Already have an account? <Link to="/login" className="text-accent hover:underline">Sign in</Link></>}>
        <EmailCode
          appId={appId}
          defaultKind={f.orgKind}
          gtm={signupGtm({ program: f.orgKind !== "consumer" ? program : undefined, invite: params.get("invite"), ref: params.get("ref") })}
          onSignedIn={async ({ orgId, kind }) => {
            if (orgId) setOrgId(orgId);
            await refresh();
            nav(safeNext ?? ((kind ?? f.orgKind) === "consumer" ? "/consumer" : "/sources/new"), { replace: true });
          }}
        />
      </AuthFrame>
    );
  }

  return (
    <AuthFrame
      title={title}
      subtitle={program && f.orgKind !== "consumer" ? "Free onboarding, $0 platform fee, and you control exactly what AI can query." : "Organisations own all data sources, datasets, keys and billing."} footer={<>Already have an account? <Link to="/login" className="text-accent hover:underline">Sign in</Link></>}>
      <form onSubmit={submit} noValidate className="space-y-4">
        <Field label="Full name" error={errors.name}>{(p) => <Input {...p} autoComplete="name" value={f.name} onChange={set("name")} />}</Field>
        <Field label="Work email" error={errors.email}>{(p) => <Input {...p} type="email" autoComplete="email" value={f.email} onChange={set("email")} />}</Field>
        <Field label="Password" hint="At least 12 characters." error={errors.password}>
          {(p) => <Input {...p} type="password" autoComplete="new-password" value={f.password} onChange={set("password")} />}
        </Field>
        <Field label="Organisation" error={errors.orgName}>{(p) => <Input {...p} autoComplete="organization" value={f.orgName} onChange={set("orgName")} />}</Field>
        <fieldset>
          <legend className="mb-1.5 text-xs font-medium text-fg-2">I want to</legend>
          <div className="grid grid-cols-3 gap-2">
            {KINDS.map((k) => (
              <label key={k.v} className={cn("cursor-pointer rounded-md border p-2 text-center text-xs transition-colors", f.orgKind === k.v ? "border-accent bg-accent-soft text-fg" : "border-border text-fg-2 hover:border-border-strong")}>
                <input type="radio" name="orgKind" value={k.v} checked={f.orgKind === k.v} onChange={() => setF((s) => ({ ...s, orgKind: k.v }))} className="sr-only" />
                <span className="block font-medium">{k.t}</span>
                <span className="text-[10px] text-fg-3">{k.d}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <ErrorNote error={error} />
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          Create account
        </Button>
      </form>
    </AuthFrame>
  );
}
